/* eslint-disable array-callback-return */
/* eslint-disable consistent-return */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal } from 'react-responsive-modal';
import 'react-responsive-modal/styles.css';

import { Form as UnformForm } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';

import { FiEdit, FiPlus, FiTrash2 } from 'react-icons/fi';

import Toolbar from '../../components/Toolbar';
import LoadingAnimation from '../../components/Loading';

import api from '../../services/api';
import formatValue from '../../utils/formatValue';
import Input from '../../components/Input';
import Button from '../../components/Button';
import SwitchComponent from '../../components/SwitchComponent';
import getValidationErrors from '../../utils/getValidationErros';
import { useToast } from '../../hooks/toast';

  import {
    Container,
    Form,
    Content,
    HeaderRow,
    Title,
    Table,
    Actions,
  IconButton,
  SectionHeader,
  SectionTitle,
  ModalTitle,
  ModalRow,
  ModalFooter,
  PrimaryButton,
} from './styles';

interface AccountData {
  id: string;
  name: string;
  type: string;
  active: boolean;
  weight: number;
  formattedWeight?: string;
}

interface SavingsAccountData {
  id: string;
  name: string;
  description: string;
  goal: number;
  type: string;
  active?: boolean;
  initial_balance?: number;
  formatted_goal: string;
}

interface ModalData {
  modalName: string;
  accountData?: AccountData;
  savingsAccountData?: SavingsAccountData;
}

interface EditSavingsAccountData {
  name: string;
  description: string;
  goal: number;
}

interface AddAccountFormData {
  description: string;
  opType: boolean; // false = IN, true = OUT
}

interface AddSavingsFormData {
  name: string;
  description: string;
  initialBalance?: number | string;
  goal: number;
}

const AccountsV2: React.FC = () => {
  const [accounts, setAccounts] = useState<AccountData[]>([]);
  const [savingsAccounts, setSavingsAccounts] = useState<SavingsAccountData[]>(
    [],
  );

  const [loading, setLoading] = useState(true);
  const [processedSavingsAccounts, setProcessedSavingsAccounts] = useState(
    false,
  );
  const [processedAccounts, setProcessedAccounts] = useState(false);

  const { addToast } = useToast();

  // ##################### MODAL DATA ################################
  const [
    isEditSavingsAccountModalOpen,
    setIsEditSavingsAccountModalOpen,
  ] = useState(false);
  const [
    editSavingsAccount,
    setEditSavingsAccount,
  ] = useState<SavingsAccountData | null>();
  const [
    isDeleteSavindsAccountModalOpen,
    setIsDeleteSavingsAccountModalOpen,
  ] = useState(false);
  const [
    deleteSavingsAccount,
    setDeleteSavingsAccount,
  ] = useState<SavingsAccountData | null>();
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(
    false,
  );
  const [deleteAccount, setDeleteAccount] = useState<AccountData | null>();
  const [isEditAccountModalOpen, setIsEditAccountModalOpen] = useState(false);
  const [editAccount, setEditAccount] = useState<AccountData | null>();

  // Create modals
  const [isAddAccountOpen, setIsAddAccountOpen] = useState(false);
  const [isAddSavingsOpen, setIsAddSavingsOpen] = useState(false);
  // ##################### MODAL DATA ################################

  const containerRef = useRef<HTMLDivElement>(null);
  const accountModalFormRef = useRef<FormHandles>(null);
  const editAccountFormRef = useRef<FormHandles>(null);
  const addAccountFormRef = useRef<FormHandles>(null);
  const addSavingsFormRef = useRef<FormHandles>(null);

  // Fetch accounts data
  const loadAccounts = useCallback(() => {
    return api.get<AccountData[]>('account').then((response) => {
      const formattedAccounts = response.data.map((acc) => {
        let formattedWeight;
        if (acc.weight > 0) {
          const weightValue = acc.weight / 100;
          const roundedWeight = weightValue.toFixed(2);
          formattedWeight = `${roundedWeight} %`;
        } else {
          formattedWeight = '--';
        }

        return {
          ...acc,
          formattedWeight,
        };
      });
      setAccounts(formattedAccounts);
      setProcessedAccounts(true);
    });
  }, []);

  // Fetch savings accounts data
  const loadSavingsAccounts = useCallback(() => {
    return api.get<SavingsAccountData[]>('savings-account').then((response) => {
      const savingsAccs = response.data
        .filter((savingsAccount) => savingsAccount.active)
        .map((savingsAccount) => {
          const formatted_goal = formatValue(savingsAccount.goal / 100);
          return {
            ...savingsAccount,
            formatted_goal,
          };
        });

      savingsAccs.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

      setSavingsAccounts(savingsAccs);
      setProcessedSavingsAccounts(true);
    });
  }, []);

  useEffect(() => {
    Promise.all([loadAccounts(), loadSavingsAccounts()]).finally(() => {
      setLoading(false);
    });
  }, [loadAccounts, loadSavingsAccounts]);

  const handleUpdateSavingsAccount = useCallback(
    async (data: EditSavingsAccountData) => {
      const editSavingsAccPayload = {
        id: editSavingsAccount?.id,
        name: data.name,
        description: data.description,
        goal: data.goal * 100,
      };

      const updatedSavingsAccount = await api.put<SavingsAccountData>(
        'savings-account',
        editSavingsAccPayload,
      );

      const newSavingsAccountIndex = savingsAccounts.findIndex(
        (savingsAccount) => savingsAccount.id === updatedSavingsAccount?.data.id,
      );

      const currentSavingsAccounts = [...savingsAccounts];

      const updatedSavingsAcc = {
        id: editSavingsAccount ? editSavingsAccount.id : 'id',
        name: data.name,
        description: data.description,
        goal: data.goal,
        type: 'SAVINGS',
        formatted_goal: formatValue(data.goal),
      } as SavingsAccountData;

      if (newSavingsAccountIndex >= 0) {
        currentSavingsAccounts.splice(newSavingsAccountIndex, 1, updatedSavingsAcc);
      }

      currentSavingsAccounts.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

      setSavingsAccounts(currentSavingsAccounts);
      setIsEditSavingsAccountModalOpen(false);
      setEditSavingsAccount(null);
    },
    [editSavingsAccount, savingsAccounts],
  );

  const handleUpdateAccount = useCallback(
    async (data: { name: string }) => {
      try {
        editAccountFormRef.current?.setErrors({});

        const schema = Yup.object().shape({
          name: Yup.string().required('Insira um nome'),
        });

        await schema.validate(data, { abortEarly: false });

        await api.put('account', {
          id: editAccount?.id,
          name: data.name,
        });

        addToast({
          type: 'success',
          title: 'Conta atualizada',
          description: 'Conta editada com sucesso!',
        });

        setIsEditAccountModalOpen(false);
        setEditAccount(null);
        loadAccounts();
      } catch (err: any) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          editAccountFormRef.current?.setErrors(errors);
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro ao editar',
          description: 'Não foi possível editar a conta.',
        });
      }
    },
    [addToast, editAccount, loadAccounts],
  );

  const handleDeleteSavingsAccount = useCallback(
    async (savingsAccountID: string) => {
      await api.delete(`savings-account/${savingsAccountID}`);

      const currentSavingsAccounts = savingsAccounts.filter(
        (s) => s.id !== savingsAccountID,
      );
      setSavingsAccounts(currentSavingsAccounts);
      setIsDeleteSavingsAccountModalOpen(false);
      setProcessedSavingsAccounts(true);
      setDeleteSavingsAccount(null);
    },
    [savingsAccounts],
  );

  const handleDeleteAccount = useCallback(async (accountID: string) => {
    await api.delete(`account/${accountID}`);
    loadAccounts().finally(() => {
      setIsDeleteAccountModalOpen(false);
      setDeleteAccount(null);
    });
  }, [loadAccounts]);

  const handleOpenModal = useCallback(
    ({ modalName, savingsAccountData, accountData }: ModalData) => {
      switch (modalName) {
        case 'deleteAccount':
          setIsDeleteAccountModalOpen(true);
          setDeleteAccount(accountData);
          break;
        case 'editAccount':
          setIsEditAccountModalOpen(true);
          setEditAccount(accountData);
          break;
        case 'editSavingsAccount':
          setIsEditSavingsAccountModalOpen(true);
          setEditSavingsAccount(savingsAccountData);
          break;
        case 'deleteSavingsAccount':
          setIsDeleteSavingsAccountModalOpen(true);
          setDeleteSavingsAccount(savingsAccountData);
          break;
        default:
          break;
      }
    },
    [],
  );

  const handleCloseModal = useCallback((modalName: string) => {
      switch (modalName) {
        case 'deleteAccount':
          setIsDeleteAccountModalOpen(false);
          setDeleteAccount(null);
          break;
        case 'editAccount':
          setIsEditAccountModalOpen(false);
          setEditAccount(null);
          break;
        case 'editSavingsAccount':
          setIsEditSavingsAccountModalOpen(false);
          setEditSavingsAccount(null);
        break;
      case 'deleteSavingsAccount':
        setIsDeleteSavingsAccountModalOpen(false);
        setDeleteSavingsAccount(null);
        break;
      default:
        break;
    }
  }, []);

  // Create account - same logic as AccountsRegister
  const handleAddAccount = useCallback(
    async (data: AddAccountFormData) => {
      try {
        addAccountFormRef.current?.setErrors({});
        const schema = Yup.object().shape({
          description: Yup.string().required('Insira uma descrição'),
        });

        await schema.validate(data, { abortEarly: false });

        const requestData = {
          name: data.description,
          type: data.opType ? 'OUT' : 'IN',
        };

        await api.post('account', requestData);

        addToast({
          type: 'success',
          title: 'Registro criado',
          description: 'Conta cadastrada com sucesso!',
        });

        setIsAddAccountOpen(false);
        addAccountFormRef.current?.reset();
        loadAccounts();
      } catch (err: any) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          addAccountFormRef.current?.setErrors(errors);
          return;
        }

        const errorMessage = err.response?.data?.message;
        if (errorMessage === 'Account already registered.') {
          addToast({
            type: 'error',
            title: 'Erro no cadastro',
            description: 'Conta já registrada',
          });
        } else {
          addToast({ type: 'error', title: 'Erro', description: 'Erro ao cadastrar conta' });
        }
      }
    },
    [addToast, loadAccounts],
  );

  // Create savings - same logic as SavingsAccountRegister
  const handleAddSavings = useCallback(
    async (data: AddSavingsFormData) => {
      try {
        addSavingsFormRef.current?.setErrors({});
        let schema;

        if (data.initialBalance) {
          schema = Yup.object().shape({
            name: Yup.string().required('Insira um nome'),
            description: Yup.string().required('Insira uma descrição'),
            initialBalance: Yup.number()
              .typeError('Insira valor numérico para o saldo inicial')
              .min(0, 'Valor da meta deve ser positivo'),
            goal: Yup.number()
              .required('Insira valor numérico para a meta')
              .typeError('Insira valor numérico para a meta')
              .positive('Valor da meta deve ser positivo'),
          });
        } else {
          schema = Yup.object().shape({
            name: Yup.string().required('Insira um nome'),
            description: Yup.string().required('Insira uma descrição'),
            goal: Yup.number()
              .required('Insira valor numérico para a meta')
              .typeError('Insira valor numérico para a meta')
              .positive('Valor da meta deve ser positivo'),
          });
        }

        await schema.validate(data, { abortEarly: false });

        await api.post('savings-account', {
          name: data.name,
          description: data.description,
          initial_balance:
            data.initialBalance === '' || data.initialBalance === undefined
              ? 0
              : Number(data.initialBalance) * 100,
          goal: Number(data.goal) * 100,
          type: 'SAVINGS',
        });

        addToast({
          type: 'success',
          title: 'Registro criado',
          description: 'Conta cadastrada com sucesso!',
        });

        setIsAddSavingsOpen(false);
        addSavingsFormRef.current?.reset();
        loadSavingsAccounts();
      } catch (err: any) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          addSavingsFormRef.current?.setErrors(errors);
          return;
        }

        const errorMessage = err.response?.data?.message;
        if (errorMessage === 'Savings account already registered.') {
          addToast({
            type: 'error',
            title: 'Erro no cadastro',
            description: 'Poupança já registrada',
          });
        } else {
          addToast({ type: 'error', title: 'Erro', description: 'Erro ao cadastrar poupança' });
        }
      }
    },
    [addToast, loadSavingsAccounts],
  );

  if (loading) {
    return (
      <Container>
        <Toolbar />
        <Form onSubmit={() => {}}>{/* spacer for top margin */}</Form>
        <div style={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LoadingAnimation />
        </div>
      </Container>
    );
  }

  return (
    <Container ref={containerRef}>
      <Toolbar />

      {/* Edit Account Modal */}
      <Modal
        open={isEditAccountModalOpen}
        showCloseIcon={false}
        onClose={() => handleCloseModal('editAccount')}
        center
        classNames={{ modal: 'v2Modal' }}
        container={containerRef.current as Element}
      >
        <ModalTitle>Editar nome da conta</ModalTitle>
        <UnformForm ref={editAccountFormRef} onSubmit={handleUpdateAccount}>
          <Input
            name="name"
            placeholder="Nome"
            defaultValue={editAccount?.name}
          />
          <ModalFooter>
            <Button type="button" onClick={() => handleCloseModal('editAccount')}>
              Cancelar
            </Button>
            <Button type="submit">Salvar alterações</Button>
          </ModalFooter>
        </UnformForm>
      </Modal>

      {/* Edit Savings Modal */}
      <Modal
        open={isEditSavingsAccountModalOpen}
        showCloseIcon={false}
        onClose={() => handleCloseModal('editSavingsAccount')}
        center
        classNames={{ modal: 'v2Modal' }}
        container={containerRef.current as Element}
      >
        <ModalTitle>Editar poupança</ModalTitle>
        <UnformForm ref={accountModalFormRef} onSubmit={handleUpdateSavingsAccount}>
          <Input
            name="name"
            placeholder="Nome"
            defaultValue={editSavingsAccount?.name}
          />
          <Input
            name="description"
            placeholder="Descrição"
            defaultValue={editSavingsAccount?.description}
          />
          <Input
            name="goal"
            placeholder="Meta"
            defaultValue={editSavingsAccount ? editSavingsAccount.goal / 100 : 0}
          />
          <ModalFooter>
            <Button type="button" onClick={() => handleCloseModal('editSavingsAccount')}>
              Cancelar
            </Button>
            <Button type="submit">Salvar alterações</Button>
          </ModalFooter>
        </UnformForm>
      </Modal>

      {/* Delete Savings Modal */}
      <Modal
        open={isDeleteSavindsAccountModalOpen}
        showCloseIcon={false}
        onClose={() => handleCloseModal('deleteSavingsAccount')}
        center
        classNames={{ modal: 'v2Modal' }}
        container={containerRef.current as Element}
      >
        <ModalTitle>Tem certeza que deseja deletar a poupança?</ModalTitle>
        <div>
          <ModalRow>
            <strong>Nome:</strong> <span>{deleteSavingsAccount?.name}</span>
          </ModalRow>
          <ModalRow>
            <strong>Descrição:</strong>{' '}
            <span>{deleteSavingsAccount?.description}</span>
          </ModalRow>
          <ModalRow>
            <strong>Meta:</strong> <span>{deleteSavingsAccount?.formatted_goal}</span>
          </ModalRow>
        </div>
        <ModalFooter>
          <Button type="button" onClick={() => handleCloseModal('deleteSavingsAccount')}>
            Cancelar
          </Button>
          <Button
            type="submit"
            onClick={() => {
              if (deleteSavingsAccount?.id) {
                handleDeleteSavingsAccount(deleteSavingsAccount?.id);
              }
            }}
          >
            Excluir poupança
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Account Modal */}
      <Modal
        open={isDeleteAccountModalOpen}
        showCloseIcon={false}
        onClose={() => handleCloseModal('deleteAccount')}
        center
        classNames={{ modal: 'v2Modal' }}
        container={containerRef.current as Element}
      >
        <ModalTitle>Tem certeza que deseja deletar a conta?</ModalTitle>
        <div>
          <ModalRow>
            <strong>Tipo:</strong>{' '}
            <span>{deleteAccount?.type === 'IN' ? 'Entrada' : 'Saída'}</span>
          </ModalRow>
          <ModalRow>
            <strong>Nome:</strong> <span>{deleteAccount?.name}</span>
          </ModalRow>
        </div>
        <ModalFooter>
          <Button type="button" onClick={() => handleCloseModal('deleteAccount')}>
            Cancelar
          </Button>
          <Button
            type="submit"
            onClick={() => {
              if (deleteAccount?.id) {
                handleDeleteAccount(deleteAccount?.id);
              }
            }}
          >
            Excluir conta
          </Button>
        </ModalFooter>
      </Modal>

      {/* Add Account Modal */}
      <Modal
        open={isAddAccountOpen}
        onClose={() => setIsAddAccountOpen(false)}
        showCloseIcon={false}
        center
        classNames={{ modal: 'v2Modal' }}
        container={containerRef.current as Element}
      >
        <ModalTitle>Criar conta</ModalTitle>
        <UnformForm ref={addAccountFormRef} onSubmit={handleAddAccount}>
          <SwitchComponent name="opType" options={[ 'Entrada', 'Saída' ]} id="op-type" />
          <Input name="description" placeholder="Nome da conta" />
          <ModalFooter>
            <Button type="button" onClick={() => setIsAddAccountOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit">Salvar</Button>
          </ModalFooter>
        </UnformForm>
      </Modal>

      {/* Add Savings Modal */}
      <Modal
        open={isAddSavingsOpen}
        onClose={() => setIsAddSavingsOpen(false)}
        showCloseIcon={false}
        center
        classNames={{ modal: 'v2Modal' }}
        container={containerRef.current as Element}
      >
        <ModalTitle>Criar poupança</ModalTitle>
        <UnformForm ref={addSavingsFormRef} onSubmit={handleAddSavings}>
          <Input name="name" placeholder="Nome" />
          <Input name="description" placeholder="Descrição" />
          <Input name="initialBalance" placeholder="Valor Inicial (opcional)" />
          <Input name="goal" placeholder="Meta" />
          <ModalFooter>
            <Button type="button" onClick={() => setIsAddSavingsOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit">Salvar</Button>
          </ModalFooter>
        </UnformForm>
      </Modal>

      <Content>
        <Form onSubmit={() => {}}>{/* spacer for top margin */}</Form>

        <HeaderRow>
          <Title>Gerenciamento de contas</Title>
        </HeaderRow>

        {/* Contas Section */}
        <SectionHeader>
          <SectionTitle>Contas</SectionTitle>
          <PrimaryButton variant="primary" onClick={() => setIsAddAccountOpen(true)}>
            <FiPlus size={16} /> Criar conta
          </PrimaryButton>
        </SectionHeader>
        <Table className="accountsTable">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Tipo</th>
              <th>Peso</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((acc) => (
              <tr key={acc.id}>
                <td data-label="Nome">{acc.name}</td>
                <td data-label="Tipo">{acc.type === 'IN' ? 'Entrada' : 'Saída'}</td>
                <td data-label="Peso">{acc.formattedWeight || '--'}</td>
                <td data-label="Ações">
                  <Actions>
                    <IconButton
                      onClick={() => handleOpenModal({ modalName: 'editAccount', accountData: acc })}
                      aria-label="Edit"
                    >
                      <FiEdit size={16} />
                    </IconButton>
                    <IconButton
                      tone="danger"
                      onClick={() => handleOpenModal({ modalName: 'deleteAccount', accountData: acc })}
                      aria-label="Delete"
                    >
                      <FiTrash2 size={16} />
                    </IconButton>
                  </Actions>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Poupanças Section */}
        <SectionHeader>
          <SectionTitle>Poupanças</SectionTitle>
          <PrimaryButton variant="primary" onClick={() => setIsAddSavingsOpen(true)}>
            <FiPlus size={16} /> Criar poupança
          </PrimaryButton>
        </SectionHeader>
        <Table>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Descrição</th>
              <th>Meta</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {savingsAccounts.map((sa) => (
              <tr key={sa.id}>
                <td data-label="Nome">{sa.name}</td>
                <td data-label="Descrição">{sa.description}</td>
                <td data-label="Meta">{sa.formatted_goal}</td>
                <td data-label="Ações">
                  <Actions>
                    <IconButton onClick={() => handleOpenModal({ modalName: 'editSavingsAccount', savingsAccountData: sa })} aria-label="Edit">
                      <FiEdit size={16} />
                    </IconButton>
                    <IconButton tone="danger" onClick={() => handleOpenModal({ modalName: 'deleteSavingsAccount', savingsAccountData: sa })} aria-label="Delete">
                      <FiTrash2 size={16} />
                    </IconButton>
                  </Actions>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Content>
    </Container>
  );
};

export default AccountsV2;
